import {
  useTheme,
  Box,
  Card,
  Typography,
  Icon,
  Theme,
  Button,
  CircularProgress,
} from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import api from '../api';
import CardScreen from '../components/CardScreen';
import paths from '../routing/paths';
import { Restaurant, Sentiment } from '../models/restaurant';
import React from 'react';
import AppButton from '../components/AppButton';
import RestaurantSearchBar from '../components/RestaurantSearchBar';
import RestaurantDetailsBottomSheet from '../components/dumb/RestaurantDetailsBottomSheet';
import CertifiedBadge from '../components/CertifiedBadge';

const tabTitles = {
  [Sentiment.Unknown]: '',
  [Sentiment.KeenToGo]: 'My List',
  [Sentiment.NotInterested]: 'Not interested',
};

interface TabItem {
  value: Sentiment;
  title: string;
  iconText: string;
  iconColor: string;
}

const buildTabs = (theme: Theme): TabItem[] => {
  return [
    {
      value: Sentiment.KeenToGo,
      title: tabTitles[Sentiment.KeenToGo],
      iconText: 'favorite',
      iconColor: theme.palette.success.main,
    },
    {
      value: Sentiment.NotInterested,
      title: tabTitles[Sentiment.NotInterested],
      iconText: 'close',
      iconColor: theme.palette.error.main,
    },
  ];
};

const UserLists = () => {
  const location = useLocation();
  const [tab] = useState<Sentiment>(
    (new URLSearchParams(location.search).get('type') as Sentiment) || Sentiment.KeenToGo,
  );
  const theme = useTheme();
  const tabs = buildTabs(theme);
  const navigate = useNavigate();
  const [keenList, setKeenList] = useState<Restaurant[]>([]);
  const [uninterestedList, setUninterestedList] = useState<Restaurant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState<Restaurant | null>(null);

  const fetchKeenList = useCallback(async () => {
    setIsLoading(true);
    const keen = await api.restaurant.getBySentiment(Sentiment.KeenToGo);
    setKeenList(keen);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (tab === Sentiment.KeenToGo) {
      fetchKeenList();
    }
    if (tab === Sentiment.NotInterested) {
      Promise.all([api.restaurant.getBySentiment(Sentiment.NotInterested)]).then(([response]) => {
        setUninterestedList(response);
        setIsLoading(false);
      });
    }
  }, [tab, fetchKeenList]);

  const handleRemoveClick = async (restaurant: Restaurant) => {
    const result = await api.sentiment.delete(restaurant.id as string, tab);

    if (!result) {
      return;
    }

    if (tab === Sentiment.KeenToGo) {
      setKeenList((prev) => prev.filter((item) => item.id !== restaurant.id));
    }

    if (tab === Sentiment.NotInterested) {
      setUninterestedList((prev) => prev.filter((item) => item.id !== restaurant.id));
    }
  };

  const buildRestaurantCard = (restaurant: Restaurant) => {
    return (
      <Card sx={{ px: 2, py: 1, mb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box
            sx={{ cursor: 'pointer', mr: 2, display: 'flex', alignItems: 'center', gap: 1 }}
            onClick={() => setSelectedRestaurant(restaurant)}
          >
            {restaurant.title}
            {restaurant.isCertified && <CertifiedBadge size={18} />}
          </Box>
          <Button onClick={() => handleRemoveClick(restaurant)}>
            <Typography
              sx={{
                borderBottom: `1px solid ${theme.palette.common.black}`,
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Remove
            </Typography>
          </Button>
        </Box>
      </Card>
    );
  };

  const emptyState = (
    <>
      <Typography mb={2} variant="body2">
        No items exist here yet
      </Typography>
      <Link to={paths.discover}>
        <AppButton>Discover</AppButton>
      </Link>
    </>
  );

  return (
    <CardScreen showBack onBackClick={() => navigate(-1)}>
      <Box display="flex" justifyContent="center" alignItems="center">
        <Typography variant="h6" mr={1}>
          {tabs.find((item) => item.value === tab)?.title}
        </Typography>
        <Icon sx={{ color: tabs.find((item) => item.value === tab)?.iconColor, fontSize: 32 }}>
          {tabs.find((item) => item.value === tab)?.iconText}
        </Icon>
      </Box>
      {isLoading && (
        <Box mt={3}>
          <CircularProgress thickness={5} size={28} />
        </Box>
      )}
      {tab === Sentiment.KeenToGo && (
        <Box mt={2}>
          <RestaurantSearchBar onRestaurantAdded={fetchKeenList} />
          {!keenList.length && !isLoading && <>{emptyState}</>}
          {keenList.map((item) => (
            <React.Fragment key={item.id}>{buildRestaurantCard(item)}</React.Fragment>
          ))}
        </Box>
      )}
      {tab === Sentiment.NotInterested && (
        <Box mt={2}>
          {!uninterestedList.length && <>{emptyState}</>}
          {uninterestedList.map((item) => (
            <React.Fragment key={item.id}>{buildRestaurantCard(item)}</React.Fragment>
          ))}
        </Box>
      )}
      <RestaurantDetailsBottomSheet
        isOpen={!!selectedRestaurant}
        onClose={() => setSelectedRestaurant(null)}
        restaurant={selectedRestaurant}
      />
    </CardScreen>
  );
};

export default UserLists;
